import React, { useEffect, useMemo, useState } from "react";

export default function RfpSheetSelectPanel({
  sheets = [],
  onStart,
  busy = false,
  error = null,
}) {
  const visible = useMemo(() => (sheets || []).filter((sheet) => !sheet.hidden), [sheets]);
  const hidden = useMemo(() => (sheets || []).filter((sheet) => sheet.hidden), [sheets]);
  const [mode, setMode] = useState("all");
  const [selected, setSelected] = useState(() => new Set(visible.map((sheet) => sheet.name)));

  useEffect(() => {
    setSelected(new Set(visible.map((sheet) => sheet.name)));
    setMode("all");
  }, [visible]);

  const toggle = (name) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const canStart = mode === "all" || selected.size > 0;

  return (
    <section className="rounded-2xl border border-emerald-200 bg-white p-4 shadow-sm dark:border-emerald-900 dark:bg-slate-900/80 sm:p-5">
      <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Select worksheets to analyze</h3>
      <p className="mt-1 text-xs text-slate-500">Hidden worksheets are not selected by default.</p>
      <div className="mt-3 space-y-2">
        <label className="flex items-center gap-2 text-sm">
          <input type="radio" name="xlsx-sheet-mode" checked={mode === "all"} onChange={() => setMode("all")} disabled={busy} />
          Analyze all worksheets
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name="xlsx-sheet-mode"
            checked={mode === "selected"}
            onChange={() => setMode("selected")}
            disabled={busy}
          />
          Analyze selected worksheets
        </label>
      </div>
      <div className={`mt-3 space-y-1 rounded-lg border border-slate-200 p-3 dark:border-slate-700 ${mode === "all" ? "opacity-60" : ""}`}>
        {visible.map((sheet) => (
          <label key={sheet.name} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={mode === "all" ? true : selected.has(sheet.name)}
              onChange={() => toggle(sheet.name)}
              disabled={busy || mode === "all"}
            />
            {sheet.name}
          </label>
        ))}
        {hidden.map((sheet) => (
          <label key={sheet.name} className="flex items-center gap-2 text-sm text-slate-500">
            <input
              type="checkbox"
              checked={mode === "selected" && selected.has(sheet.name)}
              onChange={() => toggle(sheet.name)}
              disabled={busy || mode === "all"}
            />
            {sheet.name} <span className="text-xs">(hidden)</span>
          </label>
        ))}
      </div>
      {error ? <p className="mt-2 text-xs text-red-600">{error}</p> : null}
      <button
        type="button"
        className="mt-4 rounded-lg bg-emerald-700 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
        disabled={busy || !canStart}
        onClick={() =>
          onStart({
            mode,
            selected_sheets: mode === "all" ? visible.map((sheet) => sheet.name) : [...selected],
          })
        }
      >
        {busy ? "Starting…" : "Start Analysis"}
      </button>
    </section>
  );
}
